// apps/widget/src/lib/skin-marks.ts
//
// The marks drawn on the visitor's photo for each metric: dots on spots and
// pores, strokes along lines, a glow where the skin is shiny or red.
//
// Every dot and stroke is MEASURED, here, from this photo's own pixels — a
// spot is a small patch darker than the skin around it, a line a thin dark
// valley, shine the brightest low-colour skin, redness skin redder than this
// face's own median. Nothing is placed at random: marks scattered for effect
// would read as findings, and on someone's face that is inventing them.
//
// The model's score decides how much of what is measured is shown — a good
// score shows the few strongest, a poor one more — so the photo and the number
// beside it agree. Firmness and the eye areas are shapes of the face rather
// than features on it; those are drawn as the area the score is about.
//
// All of it runs in this tab on the photo already held here. Nothing leaves.

import type { FaceRegion } from '@skinvizo/shared';
import type { CapturedFace } from '../state';
import { regionShapes } from './face-regions';
import { REGION_LANDMARKS } from './facemesh';

type Point = { x: number; y: number };

/** The analysed area: the face, cropped and scaled to this width. */
const WORK_WIDTH = 420;

export const MARK_COLOURS: Record<string, string> = {
  spots: '#2dd4bf',
  pores: '#5eead4',
  blackheads: '#0f766e',
  acne: '#ef4444',
  pigmentation: '#d97706',
  wrinkles: '#4ade80',
  texture: '#86efac',
  oiliness: '#fb923c',
  redness: '#f43f5e',
  darkCircles: '#fbbf24',
  eyeBag: '#fbbf24',
  tearTrough: '#f59e0b',
  firmness: '#f472b6',
  moisture: '#ffffff',
  radiance: '#ffffff',
};

export interface Marks {
  colour: string;
  /** Photo pixel coordinates. */
  dots: { x: number; y: number; r: number }[];
  /** A coloured image laid over `box`, for lines and glows. */
  layer: { url: string; box: Box } | null;
  /** Contour lines across the lower face (firmness). */
  contours: string[];
  /** The face outline (moisture, radiance). */
  outline: string | null;
  /** True when nothing was found to mark — show the area instead. */
  empty: boolean;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The face as measured once: luminance, redness and which pixels are skin. */
interface Maps {
  box: Box;
  /** Crop pixels per photo pixel. */
  k: number;
  W: number;
  H: number;
  lum: Float32Array;
  red: Float32Array;
  sat: Float32Array;
  skin: Uint8Array;
  capture: CapturedFace;
}

const cache = new Map<string, Promise<Maps | null>>();

/** Marks for one metric, or null when the photo cannot be read here. */
export async function marksFor(
  capture: CapturedFace,
  metric: string,
  regions: FaceRegion[],
  score: number,
): Promise<Marks | null> {
  let maps = cache.get(capture.photoUrl);
  if (!maps) {
    maps = measure(capture);
    cache.set(capture.photoUrl, maps);
  }
  const m = await maps;
  if (!m) return null;

  // 0 for an excellent score, 1 for a poor one.
  const severity = clamp((92 - score) / 55, 0.08, 1);
  const colour = MARK_COLOURS[metric] ?? '#ffffff';
  const base: Marks = { colour, dots: [], layer: null, contours: [], outline: null, empty: false };

  switch (metric) {
    case 'spots':
      return withDots(base, m, blobs(m, regions, { small: 1, large: 6, dark: true, spacing: 9, max: lerp(6, 45, severity), min: 3 }), 0.9);
    case 'pores':
      return withDots(base, m, blobs(m, regions, { small: 0, large: 2, dark: true, spacing: 4, max: lerp(25, 160, severity), min: 2 }), 0.45);
    case 'blackheads':
      return withDots(base, m, blobs(m, regions, { small: 0, large: 3, dark: true, spacing: 5, max: lerp(5, 50, severity), min: 6 }), 0.6);
    case 'acne':
      return withDots(base, m, blobs(m, regions, { small: 1, large: 7, dark: false, spacing: 12, max: lerp(2, 18, severity), min: 4 }), 1.3);
    case 'pigmentation':
      return withDots(base, m, blobs(m, regions, { small: 3, large: 14, dark: true, spacing: 20, max: lerp(3, 12, severity), min: 1.2 }), 2.4);
    case 'wrinkles':
      return withLayer(base, m, lines(m, regions, severity, { small: 1, large: 5, coverage: 0.07 + 0.08 * severity, minLength: 8 }));
    case 'texture':
      return withLayer(base, m, lines(m, ['fullFace'], severity, { small: 0, large: 2, coverage: 0.04 + 0.06 * severity, minLength: 6 }));
    case 'oiliness':
      return withLayer(base, m, shine(m, regions, severity));
    case 'redness':
      return withLayer(base, m, heat(m, regions, severity, (i) => m.red[i]!, 'above', { threshold: 0.7, opacity: 0.4 }));
    case 'darkCircles':
    case 'eyeBag':
    case 'tearTrough':
      return withLayer(base, m, heat(m, regions, severity, (i) => m.lum[i]!, 'below', { threshold: 0.3, opacity: 0.55 }));
    case 'firmness':
      return { ...base, contours: contours(m.capture, severity) };
    case 'moisture':
    case 'radiance':
      return { ...base, outline: regionShapes(m.capture.landmarks, 'fullFace', m.capture.width, m.capture.height)[0]?.d ?? null };
    default:
      return { ...base, empty: true };
  }
}

// ── measuring ───────────────────────────────────────────────────────────────

async function measure(capture: CapturedFace): Promise<Maps | null> {
  try {
    const image = new Image();
    // A kept photo arrives as a signed link on another origin; without CORS
    // its pixels cannot be read, and the marks fall back to the area.
    image.crossOrigin = 'anonymous';
    image.src = capture.photoUrl;
    await image.decode();

    const { width, height, landmarks } = capture;
    const oval = (REGION_LANDMARKS.fullFace ?? []).map((i) => landmarks[i]).filter(Boolean) as Point[];
    const xs = oval.map((p) => p.x * width);
    const ys = oval.map((p) => p.y * height);
    const pad = (Math.max(...xs) - Math.min(...xs)) * 0.04;
    const box = {
      x: Math.max(0, Math.min(...xs) - pad),
      y: Math.max(0, Math.min(...ys) - pad),
      w: 0,
      h: 0,
    };
    box.w = Math.min(width, Math.max(...xs) + pad) - box.x;
    box.h = Math.min(height, Math.max(...ys) + pad) - box.y;

    const k = WORK_WIDTH / box.w;
    const W = WORK_WIDTH;
    const H = Math.round(box.h * k);

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, box.x, box.y, box.w, box.h, 0, 0, W, H);
    const px = ctx.getImageData(0, 0, W, H).data;

    const lum = new Float32Array(W * H);
    const red = new Float32Array(W * H);
    const sat = new Float32Array(W * H);
    for (let i = 0; i < W * H; i++) {
      const r = px[i * 4]!, g = px[i * 4 + 1]!, b = px[i * 4 + 2]!;
      lum[i] = 0.299 * r + 0.587 * g + 0.114 * b;
      // Redness against the other two channels: blood shows as red without
      // the green and blue that brighter skin also carries.
      red[i] = r - (g + b) / 2;
      const max = Math.max(r, g, b);
      sat[i] = max === 0 ? 0 : (max - Math.min(r, g, b)) / max;
    }

    const skin = skinMask(capture, box, k, W, H);
    // Hair — a fringe, eyebrows' edges, a beard — is far darker than the skin
    // it sits on, and its texture reads as spots and lines. Left out.
    const skinLum = percentile(lum, skin, 0.5);
    const local = blur(lum, W, H, 3);
    for (let i = 0; i < W * H; i++) if (local[i]! < skinLum * 0.62) skin[i] = 0;
    return { box, k, W, H, lum, red, sat, skin, capture };
  } catch {
    return null;
  }
}

/** The face oval, less eyes, brows and lips: only skin is ever marked. */
function skinMask(capture: CapturedFace, box: Box, k: number, W: number, H: number): Uint8Array {
  const { landmarks, width, height } = capture;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const toCrop = (i: number) => {
    const p = landmarks[i]!;
    return { x: (p.x * width - box.x) * k, y: (p.y * height - box.y) * k };
  };
  const poly = (indices: number[]) => {
    ctx.beginPath();
    indices.forEach((i, n) => {
      const p = toCrop(i);
      if (n === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
  };

  ctx.fillStyle = '#fff';
  poly(REGION_LANDMARKS.fullFace ?? []);
  ctx.fill();

  ctx.globalCompositeOperation = 'destination-out';
  ctx.lineJoin = 'round';
  for (const [indices, padding] of EXCLUDE) {
    poly(indices);
    ctx.fill();
    ctx.lineWidth = W * padding;
    ctx.stroke();
  }

  const data = ctx.getImageData(0, 0, W, H).data;
  const alpha = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) alpha[i] = data[i * 4 + 3]! > 128 ? 1 : 0;
  // Pulled in from every edge: where skin meets hair, an eye or the
  // background the step in brightness reads as a line or a spot.
  const inner = blur(alpha, W, H, Math.round(W * 0.012));
  const mask = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) mask[i] = inner[i]! > 0.97 ? 1 : 0;
  return mask;
}

/** Contours that are not skin, with how far beyond each to keep clear. */
const EXCLUDE: [number[], number][] = [
  [[33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246], 0.015],
  [[263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466], 0.015],
  [[70, 63, 105, 66, 107, 55, 65, 52, 53, 46], 0.035],
  [[300, 293, 334, 296, 336, 285, 295, 282, 283, 276], 0.035],
  [[61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185], 0.03],
  // Nostrils: dark by anatomy, not by skin.
  [[98, 97, 2, 326, 327, 294, 278, 344, 440, 275, 4, 45, 220, 115, 48, 64], 0.0],
];

/** The metric's regions as a mask, within the skin. */
function regionMask(m: Maps, regions: FaceRegion[]): Uint8Array {
  const { capture, box, k, W, H } = m;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.setTransform(k, 0, 0, k, -box.x * k, -box.y * k);
  ctx.fillStyle = '#fff';
  for (const region of regions) {
    for (const shape of regionShapes(capture.landmarks, region, capture.width, capture.height)) {
      if (!shape.closed) continue;
      ctx.fill(new Path2D(shape.d));
    }
  }
  const data = ctx.getImageData(0, 0, W, H).data;
  const mask = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) mask[i] = data[i * 4 + 3]! > 128 && m.skin[i] ? 1 : 0;
  return mask;
}

// ── detectors ───────────────────────────────────────────────────────────────

/**
 * Small patches unlike the skin around them: a blur at the patch's size less
 * a blur at the surrounding skin's. Darker for spots, pores and pigment;
 * redder for blemishes. The strongest are kept, never closer than `spacing`.
 */
function blobs(
  m: Maps,
  regions: FaceRegion[],
  o: { small: number; large: number; dark: boolean; spacing: number; max: number; min: number },
): { x: number; y: number; strength: number }[] {
  const { W, H } = m;
  const mask = regionMask(m, regions);
  const source = o.dark ? m.lum : m.red;
  const fine = o.small > 0 ? blur(source, W, H, o.small) : source;
  const coarse = blur(source, W, H, o.large);

  const found: { x: number; y: number; strength: number }[] = [];
  const reach = Math.max(1, o.small + 1);
  for (let y = reach; y < H - reach; y++) {
    for (let x = reach; x < W - reach; x++) {
      const i = y * W + x;
      if (!mask[i]) continue;
      // Positive where the patch stands out the way this metric looks for.
      const d = o.dark ? coarse[i]! - fine[i]! : fine[i]! - coarse[i]!;
      if (d < o.min) continue;
      let peak = true;
      for (let dy = -reach; dy <= reach && peak; dy++) {
        for (let dx = -reach; dx <= reach; dx++) {
          const j = i + dy * W + dx;
          const dj = o.dark ? coarse[j]! - fine[j]! : fine[j]! - coarse[j]!;
          if (dj > d) {
            peak = false;
            break;
          }
        }
      }
      if (peak) found.push({ x, y, strength: d });
    }
  }

  found.sort((a, b) => b.strength - a.strength);
  const kept: typeof found = [];
  const limit = Math.round(o.max);
  for (const p of found) {
    if (kept.length >= limit) break;
    if (kept.every((q) => (q.x - p.x) ** 2 + (q.y - p.y) ** 2 >= o.spacing ** 2)) kept.push(p);
  }
  return kept;
}

/**
 * Thin dark valleys — lines and creases. Kept only where they run as a line:
 * a round dark patch is a spot, not a wrinkle, and is left to those metrics.
 */
function lines(
  m: Maps,
  regions: FaceRegion[],
  severity: number,
  o: { small: number; large: number; coverage: number; minLength: number },
): Uint8ClampedArray | null {
  const { W, H } = m;
  const mask = regionMask(m, regions);
  const fine = o.small > 0 ? blur(m.lum, W, H, o.small) : m.lum;
  const coarse = blur(m.lum, W, H, o.large);
  const depth = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) depth[i] = mask[i] ? coarse[i]! - fine[i]! : 0;
  // Thinned to the deepest point across each valley, so a crease is drawn as
  // a line along its floor rather than a smear the width of its shadow.
  const valley = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const v = depth[i]!;
      if (v <= 0) continue;
      const acrossX = v >= depth[i - 1]! && v >= depth[i + 1]!;
      const acrossY = v >= depth[i - W]! && v >= depth[i + W]!;
      if (acrossX || acrossY) valley[i] = v;
    }
  }

  const cut = Math.max(2, percentile(valley, mask, 1 - o.coverage));
  const on = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) on[i] = valley[i]! > cut ? 1 : 0;

  // Connected runs; a run counts as a line when it is long for its area.
  const keep = new Uint8Array(W * H);
  const seen = new Uint8Array(W * H);
  const stack: number[] = [];
  for (let start = 0; start < W * H; start++) {
    if (!on[start] || seen[start]) continue;
    const run: number[] = [];
    stack.push(start);
    seen[start] = 1;
    let minX = W, maxX = 0, minY = H, maxY = 0;
    while (stack.length) {
      const i = stack.pop()!;
      run.push(i);
      const x = i % W, y = (i - x) / W;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
          const j = ny * W + nx;
          if (on[j] && !seen[j]) {
            seen[j] = 1;
            stack.push(j);
          }
        }
      }
    }
    const length = Math.hypot(maxX - minX + 1, maxY - minY + 1);
    const thinness = length / Math.sqrt(run.length);
    if (length >= o.minLength && thinness >= 1.6) for (const i of run) keep[i] = 1;
  }

  return paint(keep, W, H, 0.85 + 0.15 * severity, 0);
}

/** Shine: the brightest, least coloured skin — light reflected off oil. */
function shine(m: Maps, regions: FaceRegion[], severity: number): Uint8ClampedArray | null {
  const { W, H } = m;
  const mask = regionMask(m, regions);
  const lum = blur(m.lum, W, H, 2);
  const cut = percentile(lum, mask, 0.84 - 0.14 * severity);
  const level = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) {
    if (!mask[i] || lum[i]! < cut) continue;
    level[i] = clamp((lum[i]! - cut) / 10 + 0.35, 0, 1) * clamp(1.5 - m.sat[i]! * 2, 0, 1);
  }
  return paint(level, W, H, 0.5 + 0.25 * severity, 5);
}

/**
 * Where a channel strays from the skin around it, inside the region —
 * redder for redness, darker for the under-eye. Measured against the same
 * face, so skin tone and lighting are not mistaken for either.
 */
function heat(
  m: Maps,
  regions: FaceRegion[],
  severity: number,
  value: (i: number) => number,
  direction: 'above' | 'below',
  o: { threshold: number; opacity: number },
): Uint8ClampedArray | null {
  const { W, H } = m;
  const mask = regionMask(m, regions);
  const channel = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) channel[i] = value(i);
  const smooth = blur(channel, W, H, 3);
  // Against the skin nearby, not the whole face: a face lit from one side is
  // darker all down that side, and that is the lamp, not the skin.
  const around = blur(channel, W, H, Math.round(W * 0.09));
  const diff = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) diff[i] = direction === 'above' ? smooth[i]! - around[i]! : around[i]! - smooth[i]!;
  const spread = Math.max(3, percentile(diff, m.skin, 0.9));

  const level = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) {
    if (!mask[i]) continue;
    level[i] = clamp(diff[i]! / spread - o.threshold + 0.3 * severity, 0, 1);
  }
  return paint(level, W, H, o.opacity + 0.3 * severity, 4);
}

/**
 * Firmness is about the face's contour, not a feature on it: lines across the
 * lower face, curving with it, denser as the score falls.
 */
function contours(capture: CapturedFace, severity: number): string[] {
  const { landmarks, width, height } = capture;
  const at = (i: number) => ({ x: landmarks[i]!.x * width, y: landmarks[i]!.y * height });
  const top = at(2).y; // under the nose
  const bottom = at(152).y; // chin
  const left = at(234).x;
  const right = at(454).x;
  const mid = (left + right) / 2;
  const count = Math.round(lerp(6, 14, severity));

  const paths: string[] = [];
  for (let n = 0; n < count; n++) {
    const t = n / (count - 1);
    const y = top + (bottom - top) * t;
    const sag = (bottom - top) * (0.06 + 0.1 * t);
    paths.push(`M${left.toFixed(1)},${(y - sag).toFixed(1)} Q${mid.toFixed(1)},${(y + sag).toFixed(1)} ${right.toFixed(1)},${(y - sag).toFixed(1)}`);
  }
  return paths;
}

// ── output ──────────────────────────────────────────────────────────────────

function withDots(base: Marks, m: Maps, found: { x: number; y: number; strength: number }[], radius: number): Marks {
  const unit = m.box.w / 100;
  return {
    ...base,
    dots: found.map((p) => ({ x: m.box.x + p.x / m.k, y: m.box.y + p.y / m.k, r: unit * radius })),
    empty: found.length === 0,
  };
}

function withLayer(base: Marks, m: Maps, alpha: Uint8ClampedArray | null): Marks {
  if (!alpha) return { ...base, empty: true };
  const canvas = document.createElement('canvas');
  canvas.width = m.W;
  canvas.height = m.H;
  const ctx = canvas.getContext('2d')!;
  const image = ctx.createImageData(m.W, m.H);
  const [r, g, b] = hex(base.colour);
  for (let i = 0; i < m.W * m.H; i++) {
    image.data[i * 4] = r;
    image.data[i * 4 + 1] = g;
    image.data[i * 4 + 2] = b;
    image.data[i * 4 + 3] = alpha[i]!;
  }
  ctx.putImageData(image, 0, 0);
  return { ...base, layer: { url: canvas.toDataURL('image/png'), box: m.box } };
}

/** 0–1 levels to alpha, softened by `soften` pixels. Null when nothing shows. */
function paint(level: ArrayLike<number>, W: number, H: number, opacity: number, soften: number): Uint8ClampedArray | null {
  const source = Float32Array.from(level as ArrayLike<number>);
  const soft = soften > 0 ? blur(source, W, H, soften) : source;
  const alpha = new Uint8ClampedArray(W * H);
  let any = false;
  for (let i = 0; i < W * H; i++) {
    const a = clamp(soft[i]! * (soften > 0 ? 1.8 : 1), 0, 1) * opacity * 255;
    alpha[i] = a;
    if (a > 8) any = true;
  }
  return any ? alpha : null;
}

// ── maths ───────────────────────────────────────────────────────────────────

/** Three box blurs: close to a Gaussian, at a cost that ignores the radius. */
function blur(src: Float32Array, W: number, H: number, r: number): Float32Array {
  let a = Float32Array.from(src);
  let b = new Float32Array(W * H);
  for (let pass = 0; pass < 3; pass++) {
    boxPass(a, b, W, H, r, true);
    boxPass(b, a, W, H, r, false);
  }
  return a;
}

function boxPass(src: Float32Array, dst: Float32Array, W: number, H: number, r: number, horizontal: boolean) {
  const lines = horizontal ? H : W;
  const len = horizontal ? W : H;
  const step = horizontal ? 1 : W;
  for (let l = 0; l < lines; l++) {
    const start = horizontal ? l * W : l;
    let sum = 0;
    for (let i = -r; i <= r; i++) sum += src[start + clampInt(i, 0, len - 1) * step]!;
    for (let i = 0; i < len; i++) {
      dst[start + i * step] = sum / (2 * r + 1);
      sum += src[start + clampInt(i + r + 1, 0, len - 1) * step]! - src[start + clampInt(i - r, 0, len - 1) * step]!;
    }
  }
}

function percentile(values: Float32Array, mask: Uint8Array, q: number): number {
  const picked: number[] = [];
  for (let i = 0; i < values.length; i += 2) if (mask[i]) picked.push(values[i]!);
  if (!picked.length) return Infinity;
  picked.sort((a, b) => a - b);
  return picked[Math.min(picked.length - 1, Math.floor(q * picked.length))]!;
}

function hex(colour: string): [number, number, number] {
  const n = parseInt(colour.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const clampInt = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
